import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MultiSelect } from 'primeng/multiselect';
import { CheckboxChangeEvent } from 'primeng/checkbox';

const MIN_VISIBLE_COLUMNS = 3;

@Component({
  selector: 'sbi-cot-back-button-table-config',
  templateUrl: './button-table-config.component.html',
  styleUrls: ['./button-table-config.component.scss'],
  host: {
    '(document:click)': 'onClick($event)',
  },
  standalone: false,
})
export class ButtonTableConfigComponent {
  @Input() set listColumns(val: any[]) {
    if (!val) {
      this.internalListColumns = val;
      return;
    }
    // Si el padre reemplaza la lista con objetos nuevos pero las mismas
    // columnas, se conservan las opciones actuales: recrearlas hace que el
    // panel re-renderice todos los checks aunque no se hayan tocado.
    if (!this._hasSameHeaders(val)) {
      this.internalListColumns = val;
    }
    this._fillSelectedList(val);
  }
  @Input() isLoading = false;
  @Input() appendTo: any = 'self';
  @Input() panelStyle: any = { width: '320px', top: '10px', left: '-180px' };
  @Output() saveConfig = new EventEmitter();
  @Output() changeConfig = new EventEmitter();

  internalListColumns = null;
  selectedListColumns = null;
  isOpen: boolean = false;
  checkAll: boolean = false;

  @ViewChild('multiSelectRef') multiSelect: MultiSelect;

  // Constructor
  constructor() {}

  private _fillSelectedList(source: any[]) {
    const visibleHeaders = new Set(
      source.filter(column => column.visible).map(column => column.header)
    );
    const selected = this.internalListColumns.filter(column =>
      visibleHeaders.has(column.header)
    );

    if (!this._isCurrentSelection(selected)) {
      this.selectedListColumns = selected;
    }
    this.checkAll = selected.length === this.internalListColumns.length;
  }

  private _hasSameHeaders(columns: any[]): boolean {
    return (
      !!this.internalListColumns &&
      this.internalListColumns.length === columns.length &&
      this.internalListColumns.every(
        (column, index) => column.header === columns[index].header
      )
    );
  }

  private _isCurrentSelection(selected: any[]): boolean {
    return (
      !!this.selectedListColumns &&
      this.selectedListColumns.length === selected.length &&
      this.selectedListColumns.every(
        (column, index) => column === selected[index]
      )
    );
  }

  onClickAll() {
    // "Seleccionar todas" solo marca; nunca desmarca. Para deseleccionar
    // se debe hacerlo columna a columna respetando el mínimo.
    this.selectedListColumns = [...this.internalListColumns];
    this.checkAll = true;
    this.changeConfig.emit(this.selectedListColumns);
  }

  onClose($event) {
    this.isOpen = false;
    this.multiSelect.hide();
    $event.preventDefault();
    $event.stopPropagation();
  }

  onClick(event) {
    let clickedElement = event.target;
    let isInsideModal = false;

    // Traverse up the DOM tree to check if the clicked element
    // or any of its ancestors have the 'p-multiselect-panel' class.
    while (clickedElement) {
      if (
        clickedElement.classList.contains('p-multiselect-overlay') ||
        clickedElement.classList.contains('p-checkbox-icon')
      ) {
        isInsideModal = true;
        break;
      }
      clickedElement = clickedElement.parentElement;
    }

    // If the click was outside the modal and the modal is open, close it.
    if (!isInsideModal && this.isOpen) {
      this.isOpen = false;
      this.multiSelect.hide();
    }
  }

  onConfigurarTabla($event) {
    this.isOpen = !this.isOpen;

    if (!this.isOpen) {
      this.multiSelect.hide();
    } else {
      this.multiSelect.hide();
      this.multiSelect.show();
    }

    $event.stopPropagation();
  }

  onPanelShow() {
    this.isOpen = true;
  }

  onPanelHide() {
    if (this.isOpen) {
      this.multiSelect.show();
    }
  }

  onSaveConfig($event) {
    this.isOpen = false;
    this.multiSelect.hide();
    $event.stopPropagation();

    this.saveConfig.emit(this.internalListColumns);
  }

  onChange($event) {
    if ($event.value.length < this._minVisibleColumns) {
      // Se restituye el ítem desmarcado conservando el orden original. Se
      // compara por header y no por referencia: el padre puede reemplazar
      // la lista con objetos nuevos y las referencias dejan de coincidir.
      this.selectedListColumns = this.internalListColumns.filter(
        column =>
          column.header === $event.itemValue.header ||
          $event.value.some(selected => selected.header === column.header)
      );
    }
    this.checkAll =
      this.selectedListColumns.length === this.internalListColumns.length;
    this.changeConfig.emit(this.selectedListColumns);
  }

  private get _minVisibleColumns(): number {
    return Math.min(MIN_VISIBLE_COLUMNS, this.internalListColumns.length);
  }
}
